import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, MapPin, Database, Lock, Users, Mail, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const sections = [
  {
    icon: Database,
    title: 'Données que nous collectons',
    items: [
      'Les informations de compte et d’identification, comme le rôle et les codes d’accès.',
      'Les données de transport et d’exploitation, comme les trajets, statuts, documents et historiques d’activité.',
      'Les données de localisation en temps réel lorsque le suivi est activé. Elles sont transmises par websockets pour l’affichage et le pilotage du trajet.',
      'Les informations techniques nécessaires au fonctionnement de l’application, comme les journaux d’erreurs et certains identifiants d’appareil ou de session.',
    ],
  },
  {
    icon: MapPin,
    title: 'Pourquoi nous les utilisons',
    items: [
      'Permettre le suivi des trajets, la gestion opérationnelle et la mise à jour des statuts en temps réel.',
      'Sécuriser l’accès à l’application et vérifier les autorisations de chaque utilisateur.',
      'Garder les données cohérentes entre le téléphone, le navigateur et le serveur, à tout moment.',
      'Améliorer la stabilité du service et diagnostiquer les incidents techniques.',
    ],
  },
  {
    icon: Lock,
    title: 'Géolocalisation et conservation',
    items: [
      'Les données GPS ne sont pas enregistrées comme historique persistant dans l’application mobile de chauffeur.',
      'Le flux de géolocalisation sert à transmettre les positions en direct au serveur et aux interfaces de supervision via websockets.',
      'Nous pouvons conserver uniquement des traces techniques ou d’audit nécessaires à la sécurité, sans stocker les points GPS bruts côté client.',
    ],
  },
  {
    icon: Lock,
    title: 'Stockage et sécurité',
    items: [
      'Certaines données peuvent être mémorisées par le téléphone ou le navigateur pour garantir la continuité du service.',
      'Nous utilisons des mesures de sécurité techniques et organisationnelles raisonnables pour limiter les accès non autorisés.',
      'Aucune information sensible n’est volontairement partagée avec des annonceurs ou des réseaux publicitaires.',
    ],
  },
  {
    icon: Users,
    title: 'Partage des données',
    items: [
      'Les données sont partagées uniquement avec les services internes nécessaires au fonctionnement de l’application et avec les utilisateurs autorisés par l’organisation.',
      'Nous pouvons divulguer des données si la loi l’exige ou pour protéger la sécurité de la plateforme et des utilisateurs.',
      'Nous ne vendons pas les données personnelles.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,109,0,0.18),_transparent_34%),linear-gradient(180deg,_#fff8f1_0%,_#ffffff_40%,_#f8fafc_100%)] text-foreground">
      <main className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="relative overflow-hidden rounded-[2rem] border border-flame/20 bg-card shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-flame via-flame to-readout" />
          <div className="absolute -right-24 top-10 h-56 w-56 rounded-full bg-flame/10 blur-3xl" />
          <div className="absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-muted blur-3xl" />
          <div className="relative grid gap-8 px-6 py-10 sm:px-10 sm:py-12 lg:grid-cols-[1.4fr_0.9fr] lg:items-end">
            <div className="max-w-3xl space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-flame/30 bg-flame/10 px-4 py-2 text-sm font-semibold text-flame-ink shadow-sm">
                <ShieldCheck className="h-4 w-4" />
                Règles de confidentialité
              </div>
              <div className="space-y-4">
                <h1 className="max-w-2xl text-4xl font-black tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                  Politique de confidentialité E-Carbu Driver
                </h1>
                <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
                  Cette page explique quelles données nous collectons, pourquoi nous les utilisons et comment
                  elles sont protégées dans l’application mobile E-Carbu Driver et les services associés.
                </p>
              </div>

              <div className="rounded-3xl border border-flame/20 bg-flame/10 p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-flame-ink">Divulgation visible dans l’application</p>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">
                  E-Carbu Driver collecte des données de localisation en arrière-plan lorsque l’application est
                  fermée ou non utilisée (when the app is not in use) pour activer le suivi des trajets, la
                  transmission GPS en temps réel, la mise à jour de la flotte et l’affichage du véhicule sur la carte.
                </p>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                  Fonctionnalités utilisant la localisation en arrière-plan: suivi de trajet actif, transmission GPS en
                  temps réel, mise à jour de la flotte et carte de suivi. Les positions sont envoyées au serveur et
                  aux superviseurs autorisés via websocket.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-border bg-muted/50 px-4 py-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Mise à jour</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">7 mai 2026</p>
                </div>
                <div className="rounded-2xl border border-border bg-muted/50 px-4 py-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Mode GPS</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">Temps réel via websocket</p>
                </div>
                <div className="rounded-2xl border border-border bg-muted/50 px-4 py-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Conservation</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">Pas d’historique GPS client</p>
                </div>
              </div>
            </div>

            <div className="relative rounded-[1.75rem] border border-border bg-readout p-5 text-slate-100 shadow-2xl shadow-slate-900/10">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-flame">Conformité Play Store</p>
                  <p className="mt-2 text-lg font-semibold text-white">Informations publiques et accessibles</p>
                </div>
                <div className="rounded-2xl bg-white/10 px-3 py-2 text-xs font-semibold text-orange-200">
                  Public
                </div>
              </div>
              <div className="grid gap-4 pt-5 text-sm text-muted-foreground/70">
                <div className="rounded-2xl bg-white/5 px-4 py-4">
                  <p className="font-semibold text-white">Websockets actifs</p>
                  <p className="mt-1 leading-6">Les données de trajet sont transmises en direct aux interfaces autorisées.</p>
                </div>
                <div className="rounded-2xl bg-white/5 px-4 py-4">
                  <p className="font-semibold text-white">Données GPS non persistées</p>
                  <p className="mt-1 leading-6">Les positions servent au suivi live et ne sont pas conservées comme historique dans l’application chauffeur.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-8 grid gap-6 lg:grid-cols-2">
          {sections.map((section) => {
            const Icon = section.icon;

            return (
              <Card key={section.title} className="group border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
                  <div className="flex items-start justify-between gap-4">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="rounded-full border border-flame/20 bg-flame/10 px-3 py-1 text-xs font-semibold text-flame-ink">
                      Données sensibles
                    </span>
                  </div>
                  <CardTitle className="text-xl text-foreground">{section.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
                  {section.items.map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl bg-muted/50 px-4 py-3">
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                      <p>{item}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}

          <Card className="border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl text-foreground">Enfants de moins de 13 ans</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
              <p>
                E-Carbu Driver est conçu pour un usage professionnel et n’est pas destiné aux enfants de moins de 13 ans.
              </p>
              <p>
                Si votre organisation utilise l’application dans un contexte impliquant des mineurs, vous devez vous
                assurer que les règles applicables à votre pays et à votre activité sont respectées avant toute collecte
                de données.
              </p>
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <CardHeader className="space-y-4 border-b border-border/60 bg-gradient-to-br from-white to-slate-50/80">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-flame to-flame text-white shadow-md shadow-orange-200/60">
                <Mail className="h-5 w-5" />
              </div>
              <CardTitle className="text-xl text-foreground">Vos droits et le contact</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 py-6 text-sm leading-7 text-muted-foreground">
              <p>
                Selon votre juridiction, vous pouvez demander l’accès, la rectification ou la suppression de vos données
                via l’administrateur de votre organisation ou le canal de support habituel d’E-Carbu.
              </p>
              <p>
                Si vous avez une question sur cette politique, contactez votre responsable interne ou le support produit
                chargé du déploiement de l’application.
              </p>
            </CardContent>
          </Card>
        </section>

        <div className="mt-8 rounded-[1.75rem] border border-border bg-readout px-6 py-6 text-slate-100 shadow-lg shadow-slate-900/20 sm:px-8">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-flame">Play Store</p>
          <p className="mt-2 text-base leading-7 text-slate-200">
            Utilisez l’URL de cette page comme lien de politique de confidentialité dans la fiche Play Store.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/"
              className="inline-flex items-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Retour à l’application
            </Link>
            <Link
              to="/account-deletion"
              className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              URL de suppression de compte
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}